import { createFileRoute } from '@tanstack/react-router';
import { DataLoadError } from '@/components/DataLoadError';
import { StatCard } from '@/components/ui/StatCard';
import { StatusBadge } from '@/components/ui/StatusBadge';
import { SYNC_RUNS } from '@/data/mock';
import { useSyncRuns } from '@/lib/use-admin-data';
import { cn } from '@/lib/utils';
import {
  ArrowClockwise,
  ArrowsLeftRight,
  Graph,
  UsersThree,
  FileText,
  CheckCircle,
  Clock,
  WarningCircle,
  Warning,
} from '@phosphor-icons/react';

const BAR_HEIGHTS = [68, 82, 55, 91, 74, 88, 96];
const BAR_LABELS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

function DashboardPage() {
  const { data: syncRuns = [], isError: syncRunsError } = useSyncRuns(5);

  return (
    <div className="space-y-6">
      {syncRunsError && <DataLoadError what="recent sync runs" />}
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-slate-900">System Overview</h2>
          <p className="text-slate-500 text-sm mt-1">
            Real-time view of pipeline health, tenant activity, and data flow across IntelliTryx.
          </p>
        </div>
        <div className="flex items-center gap-3">
          <button className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-slate-700 bg-white border border-slate-200 rounded-lg hover:bg-slate-50 transition">
            <ArrowClockwise size={18} weight="regular" />
            Trigger Normalize
          </button>
          <button className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-slate-700 bg-white border border-slate-200 rounded-lg hover:bg-slate-50 transition">
            <ArrowsLeftRight size={18} weight="regular" />
            Reconciliation
          </button>
          <button className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-primary rounded-lg hover:bg-primary/90 transition">
            <Graph size={18} weight="regular" />
            Materialize
          </button>
        </div>
      </div>

      {/* Stat Cards */}
      <div className="grid grid-cols-4 gap-4">
        <StatCard label="Active Tenants" value={142} icon={UsersThree} iconColor="text-blue-600" change="+12%" />
        <StatCard label="Total Records 24h" value="8.4M" icon={FileText} iconColor="text-emerald-600" change="+5%" />
        <StatCard
          label="Pipeline Status"
          value="Healthy"
          icon={CheckCircle}
          iconColor="text-green-600"
          change="100%"
          changeColor="text-green-500"
        />
        <StatCard
          label="Last Sync"
          value="2m ago"
          icon={Clock}
          iconColor="text-primary"
          change="LIVE"
          changeColor="text-primary"
        />
      </div>

      {/* Pipeline Activity + Active Alerts */}
      <div className="grid grid-cols-3 gap-4">
        {/* Pipeline Activity Chart */}
        <div className="col-span-2 bg-white rounded-xl border border-slate-200 p-6">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h3 className="text-lg font-bold text-slate-900">Pipeline Activity</h3>
              <p className="text-slate-500 text-xs mt-0.5">Records processed per day</p>
            </div>
            <select className="text-sm border border-slate-200 rounded-lg px-3 py-1.5 text-slate-600 bg-white">
              <option>Last 7 days</option>
              <option>Last 30 days</option>
            </select>
          </div>
          <div className="flex items-end gap-3 h-48">
            {BAR_HEIGHTS.map((h, i) => (
              <div key={i} className="flex-1 flex flex-col items-center gap-2">
                <div
                  className="w-full rounded-t-md bg-primary/80 hover:bg-primary transition-colors"
                  style={{ height: `${h}%` }}
                />
                <span className="text-xs text-slate-400 font-medium">{BAR_LABELS[i]}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Active Alerts */}
        <div className="bg-white rounded-xl border border-slate-200 p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-bold text-slate-900">Active Alerts</h3>
            <span className="text-xs text-slate-400 font-medium">3 active</span>
          </div>
          <div className="space-y-3">
            <div className="flex items-start gap-3 p-3 rounded-lg bg-rose-50 border border-rose-100">
              <WarningCircle size={20} weight="regular" className="text-rose-500 mt-0.5 shrink-0" />
              <div>
                <p className="text-sm font-semibold text-rose-700">Scheduler Failure</p>
                <p className="text-xs text-rose-500 mt-0.5">dispute-poll job failed at 12:00 UTC</p>
              </div>
            </div>
            <div className="flex items-start gap-3 p-3 rounded-lg bg-amber-50 border border-amber-100">
              <Warning size={20} weight="regular" className="text-amber-500 mt-0.5 shrink-0" />
              <div>
                <p className="text-sm font-semibold text-amber-700">Adapter Error</p>
                <p className="text-xs text-amber-500 mt-0.5">Checkout.com rate limit hit (429)</p>
              </div>
            </div>
            <div className="flex items-start gap-3 p-3 rounded-lg bg-slate-50 border border-slate-200">
              <Clock size={20} weight="regular" className="text-slate-400 mt-0.5 shrink-0" />
              <div>
                <p className="text-sm font-semibold text-slate-600">Sync Delayed</p>
                <p className="text-xs text-slate-400 mt-0.5">findaneighborhood behind by 45 min</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom Row */}
      <div className="grid grid-cols-3 gap-4">
        {/* Pipeline Metrics */}
        <div className="bg-white rounded-xl border border-slate-200 p-6">
          <h3 className="text-lg font-bold text-slate-900 mb-4">Pipeline Metrics</h3>
          <div className="space-y-4">
            {[
              { label: 'Normalization', value: 94, color: 'bg-emerald-500' },
              { label: 'Memory Usage', value: 62, color: 'bg-blue-500' },
              { label: 'Disk I/O', value: 25, color: 'bg-slate-400' },
            ].map((m) => (
              <div key={m.label}>
                <div className="flex items-center justify-between mb-1.5">
                  <span className="text-sm font-medium text-slate-600">{m.label}</span>
                  <span className="text-sm font-bold text-slate-900">{m.value}%</span>
                </div>
                <div className="w-full h-2 bg-slate-100 rounded-full overflow-hidden">
                  <div className={cn('h-full rounded-full transition-all', m.color)} style={{ width: `${m.value}%` }} />
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Recent Sync Log */}
        <div className="col-span-2 bg-white rounded-xl border border-slate-200 p-6">
          <h3 className="text-lg font-bold text-slate-900 mb-4">Recent Sync Log</h3>
          <table className="w-full">
            <thead>
              <tr className="text-xs text-slate-400 font-medium border-b border-slate-100">
                <th className="text-left pb-3">Run ID</th>
                <th className="text-left pb-3">Job Type</th>
                <th className="text-left pb-3">Status</th>
                <th className="text-right pb-3">Rows</th>
                <th className="text-right pb-3">Duration</th>
              </tr>
            </thead>
            <tbody>
              {syncRuns.slice(0, 3).map((run) => (
                <tr key={run.runId} className="border-b border-slate-50 last:border-0">
                  <td className="py-3 text-sm font-mono text-primary font-medium">{run.runId}</td>
                  <td className="py-3 text-sm text-slate-600">{run.jobType}</td>
                  <td className="py-3">
                    <StatusBadge variant={run.status} />
                  </td>
                  <td className="py-3 text-sm text-slate-700 text-right font-medium">
                    {run.rowsProcessed.toLocaleString()}
                  </td>
                  <td className="py-3 text-sm text-slate-500 text-right">{run.duration}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

export const Route = createFileRoute('/_authenticated/dashboard')({
  component: DashboardPage,
});
